// Page Tarifs publique. TOUT vient EN DIRECT de GET /orders/formats (deja
// la source utilisee par le choix de format a la creation d'un livre, voir
// CreateBookSansIA.js) — jamais un chiffre recopie qui finirait par mentir
// le jour ou la grille tarifaire (ou le pays de livraison) change.
// L'increment par page et le prix du PDF etaient autrefois recopies ici en
// dur (EUR uniquement) ; exposes par l'API depuis le chantier international
// (2026-10-02) pour ne plus avoir cette duplication a resynchroniser a la
// main — et pour qu'ils apparaissent dans la bonne devise.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listPrintFormats } from '../../services/ordersApi';
import { formatPriceCents } from '../../utils/orderWorkflow';
import Icon from '../common/Icon';
import '../../styles/luxe-theme.css';
import './TarifsLuxe.css';

// Meme formule que le moteur de tarification reel (voir le commentaire plus
// bas sur /orders/formats) : le prix de base couvre minPages, chaque tranche
// de 2 pages en plus ajoute pricePer2PagesCents.
const priceForPages = (format, pages) => {
  const extraPages = Math.max(0, pages - format.minPages);
  const steps = Math.ceil(extraPages / 2);
  return format.startingPriceCents + steps * format.pricePer2PagesCents;
};

export default function TarifsLuxe() {
  const { t, i18n } = useTranslation('legal');
  const priceLocale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
  const [formats, setFormats] = useState([]);
  const [pdfPriceCents, setPdfPriceCents] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [chargement, setChargement] = useState(true);
  const [calcFormatId, setCalcFormatId] = useState(null);
  const [calcPages, setCalcPages] = useState(30);

  useEffect(() => {
    let annule = false;
    const charger = () => {
      setChargement(true);
      listPrintFormats()
        .then((data) => {
          if (annule) return;
          setFormats(data?.formats || []);
          setPdfPriceCents(data?.pdfPriceCents || 0);
          setCurrency(data?.currency || 'EUR');
        })
        .catch(() => {})
        .finally(() => { if (!annule) setChargement(false); });
    };
    charger();
    // Le pays peut changer APRES le premier chargement (selecteur dans
    // l'en-tete, voir CountrySwitcher.js) — on redemande alors les tarifs
    // dans la nouvelle devise plutot que de laisser l'ancienne affichee.
    const onCountryChange = () => charger();
    window.addEventListener('bookipix:country-changed', onCountryChange);
    return () => {
      annule = true;
      window.removeEventListener('bookipix:country-changed', onCountryChange);
    };
  }, []);

  // Initialise le calculateur sur le format recommande (ou le premier) des
  // que les formats reels sont charges, et seulement a ce moment-la : ne
  // doit pas ecraser un choix deja fait par le visiteur si le pays change
  // ensuite (le fetch se relance alors, voir onCountryChange ci-dessus).
  useEffect(() => {
    if (!formats.length || calcFormatId != null) return;
    const initial = formats.find((f) => f.recommande) || formats[0];
    setCalcFormatId(initial.formatId);
    setCalcPages(initial.minPages);
  }, [formats, calcFormatId]);

  const formatPrix = (cents) => formatPriceCents(cents, currency, priceLocale);
  const calcFormat = formats.find((f) => f.formatId === calcFormatId) || formats[0];
  const calcExtraSteps = calcFormat ? Math.ceil(Math.max(0, calcPages - calcFormat.minPages) / 2) : 0;
  const calcSubtotal = calcFormat ? priceForPages(calcFormat, calcPages) : 0;
  const calcTotal = calcFormat ? calcSubtotal + calcFormat.shippingPriceCents : 0;

  return (
    <div className="tarifs-page">
      <div className="container-luxe tarifs-inner">
        <span className="label-gold">{t('common.eyebrow')}</span>
        <h1>{t('tarifs.title')}</h1>
        <p className="tarifs-intro">
          {t('tarifs.intro')}
        </p>

        {chargement && <p className="tarifs-chargement">{t('tarifs.loading')}</p>}

        <div className="tarifs-grid">
          {formats.map((format) => (
            <div key={format.formatId} className={`card tarifs-card ${format.recommande ? 'is-recommended' : ''}`}>
              {format.recommande && <span className="tarifs-badge">{t('tarifs.recommended')}</span>}
              <h2>{format.nom}</h2>
              <p className="tarifs-accroche">{format.accroche}</p>
              <p className="tarifs-prix">
                {formatPrix(format.startingPriceCents)}
                <span className="tarifs-prix-note">{t('tarifs.startingFrom', { count: format.minPages })}</span>
              </p>
              <ul className="tarifs-ticks">
                <li><Icon name="check" /><span>{Math.round(format.widthMm / 10)} × {Math.round(format.heightMm / 10)} cm · {t('tarifs.coverLabel', { type: format.reliure === 'rigide' ? t('tarifs.rigid') : t('tarifs.flexible') })}</span></li>
                <li><Icon name="check" /><span>{t('tarifs.thenPer2Pages', { price: formatPrix(format.pricePer2PagesCents) })}</span></li>
                <li><Icon name="check" /><span>{t('tarifs.shipping', { price: formatPrix(format.shippingPriceCents) })}</span></li>
              </ul>
              <Link to="/create-book" className={`btn tarifs-card-cta ${format.recommande ? 'btn-primary' : 'btn-outline'}`}>
                {t('tarifs.cta')}
              </Link>
            </div>
          ))}
        </div>

        <div className="tarifs-autres">
          <div className="tarifs-autre-item">
            <h3>{t('tarifs.pdfOnly')}</h3>
            <p className="tarifs-autre-prix">{formatPrix(pdfPriceCents)}</p>
            <p>{t('tarifs.pdfOnlyDescription')}</p>
          </div>
          <div className="tarifs-autre-item">
            <h3>{t('tarifs.pack')}</h3>
            <p className="tarifs-autre-prix">−10 %</p>
            <p>{t('tarifs.packDescription')}</p>
          </div>
        </div>

        {/* Calculateur (maquette "Maquettes Bookipix", 2026-10-11) : donne
            corps a la phrase "le prix affiche est exactement celui de
            l'atelier" en laissant le visiteur faire varier le nombre de
            pages avec la meme formule que le serveur. */}
        {calcFormat && (
          <div className="tarifs-calc card">
            <div>
              <span className="label-gold">{t('tarifs.calc.eyebrow')}</span>
              <h2>{t('tarifs.calc.title')}</h2>
              <p className="tarifs-intro" style={{ marginTop: 'var(--space-sm)' }}>{t('tarifs.calc.subtitle')}</p>

              <div className="tarifs-calc-seg" role="radiogroup" aria-label={t('tarifs.calc.title')}>
                {formats.map((format) => (
                  <button
                    key={format.formatId}
                    type="button"
                    role="radio"
                    aria-checked={calcFormat.formatId === format.formatId}
                    className={calcFormat.formatId === format.formatId ? 'is-active' : ''}
                    onClick={() => {
                      setCalcFormatId(format.formatId);
                      setCalcPages((prev) => Math.max(prev, format.minPages));
                    }}
                  >
                    {format.nom}
                  </button>
                ))}
              </div>

              <div className="tarifs-calc-pages">
                <span>{t('tarifs.calc.pagesLabel')}</span>
                <b>{calcPages}</b>
              </div>
              <input
                type="range"
                min={calcFormat.minPages}
                max={calcFormat.minPages + 70}
                step={2}
                value={calcPages}
                onChange={(e) => setCalcPages(Number(e.target.value))}
                aria-label={t('tarifs.calc.pagesLabel')}
              />
            </div>

            <div className="tarifs-calc-lines">
              <div>
                <span>{t('tarifs.calc.lineBase', { name: calcFormat.nom, count: calcFormat.minPages })}</span>
                <span>{formatPrix(calcFormat.startingPriceCents)}</span>
              </div>
              {calcExtraSteps > 0 && (
                <div>
                  <span>{t('tarifs.calc.lineExtra', { count: calcExtraSteps })}</span>
                  <span>{formatPrix(calcSubtotal - calcFormat.startingPriceCents)}</span>
                </div>
              )}
              <div>
                <span>{t('tarifs.calc.lineShipping')}</span>
                <span>{formatPrix(calcFormat.shippingPriceCents)}</span>
              </div>
              <div className="tarifs-calc-total">
                <span>{t('tarifs.calc.lineTotal')}</span>
                <span>{formatPrix(calcTotal)}</span>
              </div>
            </div>
          </div>
        )}

        <p className="tarifs-note">
          {t('tarifs.vatNote')}
        </p>

        <Link to="/create-book" className="btn btn-primary tarifs-cta">{t('tarifs.cta')}</Link>
      </div>
    </div>
  );
}
